import { jsx as _jsx } from "react/jsx-runtime";
import React, { memo, useCallback } from "react";
import { isEmptyTiptapDoc } from "@shared/types/tiptap-document";
import { TiptapRender } from "./tiptap-render";
import "./view.scss";
/**
 * Text-block card content.
 *
 * Three render branches, in priority order:
 *
 *   1. `content.doc` is a non-empty Tiptap document → render via the
 *      hand-rolled JSON-to-React walker (preserves paragraphs, headings,
 *      lists, marks, links). This is the path for memories captured
 *      from any rich-text surface (today: the omnibar in Add mode).
 *   2. `content.text` is non-empty → render as a single plaintext span
 *      with the existing chrome. This is the legacy / plaintext path
 *      (iOS app, iMessage, anything that didn't author a doc) and is
 *      also the fallback when a doc is structurally empty (e.g. an
 *      empty-paragraph resting state somehow made it through to
 *      persist).
 *   3. Neither has content → render the block's title as a muted
 *      placeholder.
 */
export const TextBlockView = memo(({ block, blockParams }) => {
    const textContent = block.content;
    const hasRichDoc = textContent.doc !== undefined && !isEmptyTiptapDoc(textContent.doc);
    // Bridge `blockParams.onToggleTask` (which takes `blockId` as its
    // first arg so a single closure can serve every card on screen)
    // into the renderer's per-block signature `(taskIndex, checked)`.
    // Memoised so `TiptapRender` doesn't see a new function identity
    // every render — irrelevant for correctness but keeps the
    // `React.memo` short-circuit downstream happy.
    const onToggleTaskFromBlockParams = blockParams?.onToggleTask;
    const onToggleTask = useCallback((taskIndex, nextChecked) => {
        onToggleTaskFromBlockParams?.(block.id, taskIndex, nextChecked);
    }, [block.id, onToggleTaskFromBlockParams]);
    // Only honour the interactive toggle inside the expanded detail
    // modal. On a collapsed stream card a tap on a task row should
    // expand the card (so the user gets the full editing surface and
    // can see what they're committing to), not flip the checkbox in
    // place under the user's finger — toggling a single checkbox in a
    // tiny preview without seeing the rest of the list reads as a
    // mis-tap, and the disabled-checkbox + `pointer-events: none` pair
    // in `view.scss` routes the tap through to the card's
    // expand-on-click handler. Mirrors the same `isExpanded` switch the
    // youtube / audio / email block views use to flip between their
    // facade and interactive modes.
    const isExpanded = blockParams?.isExpanded === true;
    if (hasRichDoc && textContent.doc) {
        return (_jsx(TiptapRender, { doc: textContent.doc, className: "card-content card-content--rich", onToggleTask: isExpanded && onToggleTaskFromBlockParams ? onToggleTask : undefined }));
    }
    return (_jsx("div", { className: "card-content", children: textContent.text ? (_jsx("span", { children: textContent.text })) : (_jsx("span", { className: "light-text", children: block.title })) }));
});
